import React from 'react'
import { Form, schema } from '@ggs/forms'
import { useI18n } from '@ggs/i18n'
import { useValidators } from '@ggs/hooks'
import { Button } from '../../buttons'
import { useDrupalApi } from '../../../local'
import { addNotification, useDispatch, useSelector, cleanEntityOperation } from '@ggs/store'
import { noop } from 'lodash'
import './CreateProjectForm.scss'

/**
 * @typedef {import("../../../definitions").ProjectNode} ProjectNode
 * @typedef {import("../../../definitions").GenericNode} GenericNode
 */

/**
 * @typedef {Object} CreateProjectFormProps
 * @property {function=} closeModal Close modal handler, required when form displaying in a modal.
 * @property {string=} operationId ID of add resource operation to resume.
 */

/**
 * Create new project form.
 * @param {CreateProjectFormProps} props
 * @return {JSX.Element}
 */
export function CreateProjectForm({ closeModal = noop, operationId = '' }) {
  const { t } = useI18n()
  const validators = useValidators()
  const { createProject, addResourceToProject } = useDrupalApi()
  const dispatch = useDispatch()
  const entityOperation = useSelector((state) => state.settings.entityOperation[operationId])

  /**
   * Abort create project operation.
   */
  const abortOperation = () => {
    closeModal()
  }

  /**
   * Process add resource to project.
   * @param {ProjectNode} projectNode
   * @param {GenericNode} resourceNode
   * @return {Promise<void>}
   */
  const processAddResourceToProject = async (projectNode, resourceNode) => {
    const updatedProjectNode = await addResourceToProject(projectNode, resourceNode)
    if (updatedProjectNode) {
      dispatch(
        addNotification(
          'add_resource_success',
          'success',
          t('Resource "{{resourceName}}" was added to "{{projectName}}".', {
            resourceName: resourceNode.title || resourceNode.name,
            projectName: projectNode.title,
          })
        )
      )
    } else {
      dispatch(
        addNotification(
          'add_resource_error',
          'error',
          t('Add resource "{{resourceName}}" to "{{projectName}} failed.', {
            resourceName: resourceNode.title,
            projectName: projectNode.title,
          })
        )
      )
    }

    dispatch(cleanEntityOperation(operationId))
  }

  /**
   * Resume entity operation post project creation.
   * @param {ProjectNode} projectNode
   * @return {boolean} TRUE when operation was resumed, false otherwise.
   */
  const resumeEntityOperation = (projectNode) => {
    if (!entityOperation) {
      return false
    }

    const { operation, attributes } = entityOperation
    switch (operation) {
      case 'add':
        processAddResourceToProject(projectNode, /** @type {GenericNode} */ (attributes)).then(() =>
          console.log('Resource added to project.')
        )
        closeModal()
        return true

      default:
        console.error(`Operation ${entityOperation.operation} not supported`)
    }

    return false
  }

  /**
   * Handle project creation.
   * @param {Object<string, any>} values
   * @param {any} formik
   * @return {Promise<void>}
   */
  const onSubmitHandler = async (values, { setSubmitting }) => {
    const projectNode = await createProject(values.name, values.description)

    if (projectNode) {
      dispatch(
        addNotification(
          'create_project_success',
          'success',
          t('Your project "{{name}}" was created.', { name: projectNode.title })
        )
      )

      const resumed = resumeEntityOperation(projectNode)
      // When no operation resumed, take control for closing the modal.
      if (!resumed) {
        closeModal()
      }
    } else {
      dispatch(addNotification('create_project_error', 'error', t('Project creation failed.')))
    }

    setSubmitting(false)
  }

  const fieldsDefinition = {
    name: {
      label: t('Project Name'),
      type: schema.Forms.FieldTypes.email,
      validator: validators.title,
      fullWidth: true,
      value: '',
    },
    description: {
      label: t('Project Description'),
      type: schema.Forms.FieldTypes.textarea,
      inputProps: {
        placeholder: t('Enter a project name'),
      },
      fullWidth: true,
      labelProps: {
        shrink: true,
      },
    },
  }

  const CreateProjectForm = Form.useForm({
    withDraftState: false,
    formId: 'form-project__create',
    className: 'form-project__create',
    // @ts-ignore
    fields: fieldsDefinition,
    actions: [
      {
        type: 'reset',
        label: t('Cancel'),
        variant: Button.variant.contained,
        onClick: abortOperation,
        className: 'form__action-cancel'
      },
      {
        type: 'submit',
        label: t('Save Project'),
        variant: Button.variant.contained,
      },
    ],
    sx: {},
    onSubmit: onSubmitHandler,
  })

  return <div className="create-project">{CreateProjectForm.FormComp}</div>
}
